import { useState, type FormEvent, type ReactNode } from 'react';
import type { Purchase, Tasting, TypeDeVin, Wine, WineDetails } from '../domain/cellar';
import { canonicalCepage } from '../domain/grapes';
import { formatMonthShort, todayInParis, type MonthValue } from '../domain/months';
import { DeleteConfirmation } from '../components/DeleteConfirmation';
import { NumberStepper } from '../components/NumberStepper';
import {
  PurchaseFields,
  dayText,
  defaultPurchaseFields,
  eurosToCents,
  purchaseInputFrom,
  wholeEuros,
  type PurchaseFormValue,
} from '../components/PurchaseFields';
import { WineIdentityFields, type WineIdentityFormValue } from '../components/WineIdentityFields';
import { DRINK_BY_YEARS_AHEAD, DRINK_BY_YEARS_BACK, YearPicker } from '../components/YearPicker';
import { bottleCount, countryLabel, formatWholeEuros, regionLabel, shortDay, t, tf } from '../i18n';
import { userMessage, wineTypeLabel } from '../i18n/errors';

/**
 * The sections of a wine's page (owner request, 2026-10-09): each shows its values as plain text
 * with one "Modifier" button at its top, which turns the whole section into a form, saved or
 * cancelled at once. A refused value is said in the section and the form stays open with what
 * was typed (User Story 7, FR-029). Prices read rounded to the euro.
 */

/** Whole numbers only: "" or "3,5" become NaN so the rule refuses them. */
const wholeNumber = (text: string) => (/^\s*\d+\s*$/.test(text) ? Number(text) : Number.NaN);

/** A price's draft in whole euros (owner, 2026-10-15: no decimals typed or shown). */
const centsToDraft = (cents: number) => String(Math.round(cents / 100));

/** A purchase's date: its day when known (schema version 6), else its month. */
const purchaseDateText = (purchase: Purchase) =>
  purchase.purchasedDay !== undefined
    ? shortDay({ ...purchase.purchasedOn, day: purchase.purchasedDay })
    : formatMonthShort(purchase.purchasedOn);
/** The date picker's draft: the day, or the 1st of the month when the day is not known. */
const purchaseDayDraft = (purchase: Purchase) =>
  dayText({ ...purchase.purchasedOn, day: purchase.purchasedDay ?? 1 });

/**
 * Runs a save: a refusal thrown at once or later is shown; `onDone` runs once the change is
 * accepted (it is then saved in the background, like every change).
 */
function useSave(errorMessage: (error: unknown) => string = userMessage) {
  const [error, setError] = useState<string | undefined>();
  function run(save: () => Promise<unknown>, onDone: () => void) {
    setError(undefined);
    try {
      save().catch((failure: unknown) => setError(errorMessage(failure)));
      onDone();
    } catch (refused) {
      setError(errorMessage(refused));
    }
  }
  return { error, setError, run };
}

/** A section's title with its "Modifier" button, hidden while the section is a form. */
function SectionHead({
  id,
  title,
  editing,
  onEdit,
  level = 2,
}: {
  id: string;
  title: string;
  editing: boolean;
  onEdit: () => void;
  level?: 2 | 3;
}) {
  const Heading = level === 2 ? 'h2' : 'h3';
  return (
    <div className="wine-detail__head">
      <Heading id={id}>{title}</Heading>
      {!editing && (
        <button
          type="button"
          className="wine-detail__edit"
          aria-label={tf('edit.field', { field: title })}
          onClick={onEdit}
        >
          {t('edit.open')}
        </button>
      )}
    </div>
  );
}

/** Label and value pairs, read only. */
function Values({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="wine-detail__values">
      {rows.map(([label, value]) => (
        <div key={label} className="wine-detail__value">
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function FormActions({ onCancel }: { onCancel: () => void }) {
  return (
    <div className="wine-detail__actions">
      <button type="submit">{t('common.save')}</button>
      <button type="button" onClick={onCancel}>
        {t('common.cancel')}
      </button>
    </div>
  );
}

function ErrorLine({ error }: { error: string | undefined }) {
  return error ? <p role="alert">{error}</p> : null;
}

type WineDraft = WineIdentityFormValue & { drinkUntilYear: string };

const wineDraft = (wine: Wine): WineDraft => ({
  typeDeVin: wine.typeDeVin,
  domaine: wine.domaine,
  parcelle: wine.parcelle ?? '',
  annee: String(wine.annee),
  pays: wine.pays,
  region: wine.region ?? '',
  cepage: wine.cepage,
  drinkUntilYear: wine.drinkUntilYear !== undefined ? String(wine.drinkUntilYear) : '',
});

/**
 * "Détails du vin": Type, Domaine, Parcelle, Année, Pays, Région, Cépage, the bottles left (owner,
 * 2026-10-15; read only, it comes from the purchases and tastings), À boire avant.
 */
export function WineFieldsSection({
  wine,
  stock,
  grapes,
  onSave,
  errorMessage,
}: {
  wine: Wine;
  stock: number;
  grapes: readonly string[];
  onSave: (details: WineDetails) => Promise<unknown>;
  errorMessage: (error: unknown) => string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<WineDraft>(() => wineDraft(wine));
  const { error, setError, run } = useSave(errorMessage);
  const thisYear = todayInParis().year;

  function open() {
    setDraft(wineDraft(wine));
    setError(undefined);
    setEditing(true);
  }

  function save(event: FormEvent) {
    event.preventDefault();
    const details: WineDetails = {
      domaine: draft.domaine,
      cepage: canonicalCepage(draft.cepage, grapes),
      parcelle: draft.parcelle,
      annee: wholeNumber(draft.annee),
      pays: draft.pays,
      ...(draft.region !== '' ? { region: draft.region } : {}),
      typeDeVin: draft.typeDeVin as TypeDeVin,
      ...(draft.drinkUntilYear !== '' ? { drinkUntilYear: wholeNumber(draft.drinkUntilYear) } : {}),
      ...(wine.drinkFromYear !== undefined ? { drinkFromYear: wine.drinkFromYear } : {}),
    };
    run(
      () => onSave(details),
      () => setEditing(false),
    );
  }

  return (
    <section className="wine-detail__section" aria-labelledby="wine-fields">
      <SectionHead id="wine-fields" title={t('wine.legend')} editing={editing} onEdit={open} />
      <ErrorLine error={error} />
      {editing ? (
        <form className="wine-detail__form" onSubmit={save}>
          <WineIdentityFields
            value={draft}
            onChange={(identity) => setDraft({ ...draft, ...identity })}
            grapes={grapes}
            hideLegend
          >
            <label htmlFor="drinkUntilYear">{t('drinkUntil.legend')}</label>
            <YearPicker
              buttonId="drinkUntilYear"
              value={draft.drinkUntilYear}
              onChange={(drinkUntilYear) => setDraft({ ...draft, drinkUntilYear })}
              from={thisYear - DRINK_BY_YEARS_BACK}
              to={thisYear + DRINK_BY_YEARS_AHEAD}
              suggested={thisYear + 10}
            />
          </WineIdentityFields>
          <FormActions
            onCancel={() => {
              setError(undefined);
              setEditing(false);
            }}
          />
        </form>
      ) : (
        <Values
          rows={[
            [t('wine.typeDeVin'), wineTypeLabel(wine.typeDeVin)],
            [t('wine.domaine'), wine.domaine],
            [t('wine.parcelle'), wine.parcelle || t('edit.none')],
            [t('wine.annee'), String(wine.annee)],
            [t('wine.pays'), countryLabel(wine.pays)],
            // Required since 2026-10-03; a wine saved before shows "À compléter".
            [
              t('wine.region'),
              wine.region !== undefined ? regionLabel(wine.region) : t('wine.regionMissing'),
            ],
            [t('wine.cepage'), wine.cepage],
            [t('purchase.quantity'), bottleCount(stock)],
            [
              t('drinkUntil.legend'),
              wine.drinkUntilYear !== undefined ? String(wine.drinkUntilYear) : t('edit.none'),
            ],
          ]}
        />
      )}
    </section>
  );
}

export interface PurchaseChange {
  quantity: number;
  unitPriceCents: number;
  purchasedOn: MonthValue;
  purchasedDay?: number;
  notes: string;
}

/** One purchase, with its own "Modifier": date, quantity (− and +), price, details; delete. */
export function PurchaseRecord({
  purchase,
  onSave,
  onDelete,
}: {
  purchase: Purchase;
  onSave: (change: PurchaseChange) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
}) {
  const [editing, setEditing] = useState(false);
  const [day, setDay] = useState(purchaseDayDraft(purchase));
  const [quantity, setQuantity] = useState(purchase.quantity);
  const [price, setPrice] = useState(centsToDraft(purchase.unitPriceCents));
  const [notes, setNotes] = useState(purchase.notes ?? '');
  const { error, setError, run } = useSave();
  const headId = `purchase-${purchase.id}`;

  function open() {
    setDay(purchaseDayDraft(purchase));
    setQuantity(purchase.quantity);
    setPrice(centsToDraft(purchase.unitPriceCents));
    setNotes(purchase.notes ?? '');
    setError(undefined);
    setEditing(true);
  }

  function save(event: FormEvent) {
    event.preventDefault();
    // An older purchase saved without its day keeps it unknown unless the date is changed.
    const [year = '', month = '', dayOfMonth = ''] = day.split('-');
    const date =
      day === purchaseDayDraft(purchase)
        ? {
            purchasedOn: purchase.purchasedOn,
            ...(purchase.purchasedDay !== undefined ? { purchasedDay: purchase.purchasedDay } : {}),
          }
        : {
            purchasedOn: { year: wholeNumber(year), month: wholeNumber(month) },
            purchasedDay: wholeNumber(dayOfMonth),
          };
    run(
      () => onSave({ ...date, quantity, unitPriceCents: eurosToCents(price), notes }),
      () => setEditing(false),
    );
  }

  return (
    <li className="wine-detail__record" data-purchase={purchase.id} aria-labelledby={headId}>
      <SectionHead
        id={headId}
        level={3}
        title={tf('purchase.of', { date: purchaseDateText(purchase) })}
        editing={editing}
        onEdit={open}
      />
      <ErrorLine error={error} />
      {editing ? (
        <form className="wine-detail__form" onSubmit={save}>
          <label htmlFor={`${headId}-date`}>{t('purchase.date')}</label>
          <input
            id={`${headId}-date`}
            type="date"
            required
            max={dayText(todayInParis())}
            value={day}
            onChange={(event) => setDay(event.target.value)}
          />
          <NumberStepper
            id={`${headId}-quantity`}
            label={t('purchase.quantity')}
            value={quantity}
            decreaseLabel={t('drink.bottlesLess')}
            increaseLabel={t('drink.bottlesMore')}
            onChange={setQuantity}
          />
          <label htmlFor={`${headId}-price`}>{t('purchase.unitPrice')}</label>
          <input
            id={`${headId}-price`}
            inputMode="numeric"
            required
            value={price}
            onChange={(event) => setPrice(wholeEuros(event.target.value))}
          />
          <label htmlFor={`${headId}-notes`}>{t('purchase.notes')}</label>
          <textarea
            id={`${headId}-notes`}
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
          <FormActions
            onCancel={() => {
              setError(undefined);
              setEditing(false);
            }}
          />
          <DeleteConfirmation
            label={t('purchase.delete')}
            warning={t('purchase.deleteWarning')}
            onConfirm={onDelete}
          />
        </form>
      ) : (
        <Values
          rows={[
            // The date is in the record's title.
            [t('purchase.quantity'), String(purchase.quantity)],
            [t('purchase.unitPrice'), formatWholeEuros(purchase.unitPriceCents)],
            ...(purchase.notes
              ? ([[t('purchase.notes'), purchase.notes]] as [string, ReactNode][])
              : []),
          ]}
        />
      )}
    </li>
  );
}

export interface TastingChange {
  tastedOn: MonthValue;
  rating: number | null;
  notes: string;
}

/** "2026-10" for the month picker. */
const monthText = ({ year, month }: MonthValue) => `${year}-${String(month).padStart(2, '0')}`;

/**
 * A tasting's form (opened by its "Modifier" in the history): the month and year in one picker
 * (owner, 2026-10-15, like the purchase's date; a tasting keeps no day), the rating as 1, 1.5…
 * like the rating filter, and the notes. Nothing takes the focus when it opens.
 */
export function TastingEditForm({
  tasting,
  onSave,
  onDelete,
  onClose,
}: {
  tasting: Tasting;
  onSave: (change: TastingChange) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
  onClose: () => void;
}) {
  const [when, setWhen] = useState(monthText(tasting.tastedOn));
  const [rating, setRating] = useState(tasting.rating !== undefined ? String(tasting.rating) : '');
  const [notes, setNotes] = useState(tasting.notes ?? '');
  const { error, run } = useSave();
  const id = `tasting-${tasting.id}`;

  function save(event: FormEvent) {
    event.preventDefault();
    const [year = '', month = ''] = when.split('-');
    run(
      () =>
        onSave({
          tastedOn: { year: wholeNumber(year), month: wholeNumber(month) },
          rating: rating === '' ? null : Number(rating),
          notes,
        }),
      onClose,
    );
  }

  return (
    <form className="wine-detail__form" onSubmit={save}>
      <ErrorLine error={error} />
      <label htmlFor={`${id}-month`}>{t('tasting.date')}</label>
      <input
        id={`${id}-month`}
        type="month"
        required
        max={monthText(todayInParis())}
        value={when}
        onChange={(event) => setWhen(event.target.value)}
      />
      <label htmlFor={`${id}-rating`}>{t('drink.rating')}</label>
      <select
        id={`${id}-rating`}
        value={rating}
        onChange={(event) => setRating(event.target.value)}
      >
        <option value="">{t('tasting.noRating')}</option>
        {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map((r) => (
          <option key={r} value={String(r)}>
            {String(r)}
          </option>
        ))}
      </select>
      <label htmlFor={`${id}-notes`}>{t('tasting.notes')}</label>
      <textarea
        id={`${id}-notes`}
        rows={3}
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
      />
      <FormActions onCancel={onClose} />
      <DeleteConfirmation
        label={t('tasting.delete')}
        warning={t('tasting.deleteWarning')}
        onConfirm={onDelete}
      />
    </form>
  );
}

/**
 * "Ajouter une bouteille" on a wine's page (owner request, 2026-10-15): the purchase form of a new
 * bottle, recorded on this wine. A quantity of 0 is a tasting elsewhere, as on the purchase page.
 */
export function AddPurchaseForm({
  onSave,
  onClose,
}: {
  onSave: (purchase: ReturnType<typeof purchaseInputFrom>) => Promise<unknown>;
  onClose: () => void;
}) {
  const [value, setValue] = useState<PurchaseFormValue>(() => defaultPurchaseFields());
  const { error, run } = useSave();

  function save(event: FormEvent) {
    event.preventDefault();
    run(() => onSave(purchaseInputFrom(value)), onClose);
  }

  return (
    <section className="wine-detail__section wine-detail__add-section">
      <form className="wine-detail__form wine-detail__add" onSubmit={save}>
        <ErrorLine error={error} />
        <PurchaseFields value={value} onChange={setValue} />
        <FormActions onCancel={onClose} />
      </form>
    </section>
  );
}
